/**
 * Extractors for `getPlantLineOperations` — LineOperation bundles with
 * labor/equipment participation, plus the legacy HumanLabor fallback.
 */

import { extractNode, extractNodeList } from '../graph-row'
import { compareByNameThenId } from '../sort'
import type {
  CypherReadResult,
  PlantCrewRequirementRef,
  PlantEquipment,
  PlantHumanLaborRef,
  PlantLaborStaffing,
  PlantLaborTagRef,
  PlantLaborTrigger,
  PlantLineCrewRequirement,
  PlantLineLaborStation,
  PlantLineOperation,
  PlantLineOperationRef,
  PlantLineOperations,
  PlantOperationEquipment,
  PlantOperationLabor,
  PlantOperationOrderCoverage,
  PlantRoleRef,
  PlantScheduleProfileRef
} from '../types'
import { extractPlantEdges } from './edges'
import { extractProcedureList } from './equipment-relationships'

function asRecord(value: unknown): Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return {}
  return value as Record<string, unknown>
}

function optionalNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : undefined
  }
  return undefined
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function optionalBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined
}

function compareHumanLabor(a: PlantHumanLaborRef, b: PlantHumanLaborRef): number {
  const aSequence = optionalNumber(a.sequence_number)
  const bSequence = optionalNumber(b.sequence_number)
  if (aSequence !== undefined && bSequence !== undefined && aSequence !== bSequence) {
    return aSequence - bSequence
  }
  if (aSequence !== undefined) return -1
  if (bSequence !== undefined) return 1
  return compareByNameThenId(a, b)
}

function compareLineOperation(a: PlantLineOperationRef, b: PlantLineOperationRef): number {
  const aSequence = optionalNumber(a.sequence_number)
  const bSequence = optionalNumber(b.sequence_number)
  if (aSequence !== undefined && bSequence !== undefined && aSequence !== bSequence) {
    return aSequence - bSequence
  }
  if (aSequence !== undefined) return -1
  if (bSequence !== undefined) return 1
  return compareByNameThenId(a, b)
}

function extractStaffing(value: unknown): PlantLaborStaffing[] {
  if (!Array.isArray(value)) return []
  const rows: PlantLaborStaffing[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const role = extractNode<PlantRoleRef>(row['role'])
    if (!role) continue
    const props = asRecord(row['props'])
    const staffing: PlantLaborStaffing = { role }
    const minGrade = optionalNumber(props['min_grade'])
    const maxGrade = optionalNumber(props['max_grade'])
    if (minGrade !== undefined) staffing.minGrade = minGrade
    if (maxGrade !== undefined) staffing.maxGrade = maxGrade
    rows.push(staffing)
  }
  return rows.sort((a, b) => compareByNameThenId(a.role, b.role))
}

function extractTrigger(value: unknown): PlantLaborTrigger | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  const tag = extractNode<PlantLaborTagRef>(row['tag'])
  if (!tag) return null
  const props = asRecord(row['props'])
  const trigger: PlantLaborTrigger = { tag }
  const conditionJson = optionalString(props['condition_json'])
  if (conditionJson !== undefined) trigger.conditionJson = conditionJson
  return trigger
}

function extractHumanLaborRef(value: unknown): PlantHumanLaborRef | null {
  const raw = extractNode<PlantHumanLaborRef>(value)
  if (!raw) return null
  const sequenceNumber = optionalNumber(raw.sequence_number)
  return sequenceNumber === undefined ? raw : { ...raw, sequence_number: sequenceNumber }
}

function extractLineOperationRef(value: unknown): PlantLineOperationRef | null {
  const raw = extractNode<PlantLineOperationRef>(value)
  if (!raw) return null
  const sequenceNumber = optionalNumber(raw.sequence_number)
  return sequenceNumber === undefined ? raw : { ...raw, sequence_number: sequenceNumber }
}

function extractIdList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value
    .filter((item): item is string => typeof item === 'string' && item.length > 0)
    .sort((a, b) => a.localeCompare(b))
}

function extractCrewRequirement(value: unknown): PlantLineCrewRequirement | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  const rawCrew = extractNode<PlantCrewRequirementRef>(row['crew'])
  if (!rawCrew) return null
  const headcount = optionalNumber(rawCrew.headcount)
  const crew = headcount === undefined ? rawCrew : { ...rawCrew, headcount }

  return {
    crew,
    staffing: extractStaffing(row['staffing']),
    coveredOperations: (Array.isArray(row['coveredOperations']) ? row['coveredOperations'] : [])
      .map((item) => extractHumanLaborRef(item))
      .filter((node): node is PlantHumanLaborRef => node !== null)
      .sort(compareByNameThenId)
  }
}

function extractCrewRequirements(value: unknown): PlantLineCrewRequirement[] {
  if (!Array.isArray(value)) return []
  return value
    .map(extractCrewRequirement)
    .filter((crew): crew is PlantLineCrewRequirement => crew !== null)
    .sort((a, b) => compareByNameThenId(a.crew, b.crew))
}

function extractOperationLabor(value: unknown): PlantOperationLabor | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  const task = extractHumanLaborRef(row['task'])
  if (!task) return null

  const edges = extractPlantEdges([row['edge']])
  const edge = edges[0]
  const numEmployees = optionalNumber(edge?.props['num_employees'])

  const labor: PlantOperationLabor = {
    task,
    staffing: extractStaffing(row['staffing']),
    schedules: extractNodeList<PlantScheduleProfileRef>(row['schedules']).sort(compareByNameThenId),
    trigger: extractTrigger(row['trigger']),
    procedures: extractProcedureList(row['procedures'])
  }
  if (numEmployees !== undefined) labor.numEmployees = numEmployees
  return labor
}

function extractOperationEquipment(value: unknown): PlantOperationEquipment | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  const equipment = extractNode<PlantEquipment>(row['equipment'])
  if (!equipment) return null
  const usageKind = optionalString(row['usageKind'])
  const participation: PlantOperationEquipment = { equipment }
  if (usageKind !== undefined) participation.usageKind = usageKind
  return participation
}

function extractStation(value: unknown): PlantLineLaborStation | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  const task = extractHumanLaborRef(row['task'])
  if (!task) return null

  const edges = extractPlantEdges([row['edge']])
  const edge = edges[0]
  const numEmployees = optionalNumber(edge?.props['num_employees'])

  const station: PlantLineLaborStation = {
    task,
    staffing: extractStaffing(row['staffing']),
    schedules: extractNodeList<PlantScheduleProfileRef>(row['schedules']).sort(compareByNameThenId),
    trigger: extractTrigger(row['trigger']),
    procedures: extractProcedureList(row['procedures']),
    usedEquipment: extractNodeList<PlantEquipment>(row['usedEquipment']).sort(compareByNameThenId)
  }
  if (numEmployees !== undefined) station.numEmployees = numEmployees
  return station
}

function extractStations(value: unknown): PlantLineLaborStation[] {
  if (!Array.isArray(value)) return []
  return value
    .map(extractStation)
    .filter((station): station is PlantLineLaborStation => station !== null)
    .sort((a, b) => compareHumanLabor(a.task, b.task))
}

type RawLineOperation = PlantLineOperation & { isException: boolean }

function extractRawLineOperation(value: unknown): RawLineOperation | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  const operation = extractLineOperationRef(row['operation'])
  if (!operation) return null

  return {
    operation,
    labor: (Array.isArray(row['labor']) ? row['labor'] : [])
      .map(extractOperationLabor)
      .filter((item): item is PlantOperationLabor => item !== null)
      .sort((a, b) => compareByNameThenId(a.task, b.task)),
    equipment: (Array.isArray(row['equipment']) ? row['equipment'] : [])
      .map(extractOperationEquipment)
      .filter((item): item is PlantOperationEquipment => item !== null)
      .sort((a, b) => compareByNameThenId(a.equipment, b.equipment)),
    procedures: extractProcedureList(row['procedures']),
    schedules: extractNodeList<PlantScheduleProfileRef>(row['schedules']).sort(compareByNameThenId),
    trigger: extractTrigger(row['trigger']),
    feedsIn: extractIdList(row['feedsIn']),
    feedsOut: extractIdList(row['feedsOut']),
    recoveredBy: (Array.isArray(row['recoveredBy']) ? row['recoveredBy'] : [])
      .map(extractLineOperationRef)
      .filter((item): item is PlantLineOperationRef => item !== null)
      .sort(compareByNameThenId),
    fallbacks: (Array.isArray(row['fallbacks']) ? row['fallbacks'] : [])
      .map(extractLineOperationRef)
      .filter((item): item is PlantLineOperationRef => item !== null)
      .sort(compareByNameThenId),
    isException: optionalBoolean(row['isException']) === true
  }
}

function computeOrderCoverage(operations: PlantLineOperation[]): PlantOperationOrderCoverage {
  if (operations.length === 0) return 'none'
  const sequences = operations.map((op) => optionalNumber(op.operation.sequence_number))
  const numbered = sequences.filter((value): value is number => value !== undefined)
  if (numbered.length === 0) return 'none'
  if (numbered.length === operations.length && new Set(numbered).size === operations.length) {
    return 'full'
  }
  return 'partial'
}

export function mapPlantLineOperations(
  raw: CypherReadResult,
  _lineId: string
): PlantLineOperations {
  const row = raw.results[0] ?? {}
  const allOps = (Array.isArray(row['operations']) ? row['operations'] : [])
    .map(extractRawLineOperation)
    .filter((op): op is RawLineOperation => op !== null)

  const primary = allOps
    .filter((op) => !op.isException)
    .map(({ isException: _ignored, ...op }) => op)
    .sort((a, b) => compareLineOperation(a.operation, b.operation))

  const legacyStations = extractStations(row['stations'])
  const crewRequirements = extractCrewRequirements(row['crewRequirements'])
  const legacy = {
    stations: legacyStations,
    flowStations: (Array.isArray(row['flowStations']) ? row['flowStations'] : [])
      .map((item) => extractHumanLaborRef(item))
      .filter((node): node is PlantHumanLaborRef => node !== null)
      .sort(compareHumanLabor),
    flowEdges: extractPlantEdges(row['flowEdges']).filter((edge) => edge.type === 'FEEDS'),
    flowNeighbors: extractNodeList<PlantEquipment>(row['flowNeighbors']).sort(compareByNameThenId)
  }

  if (primary.length > 0) {
    return {
      mode: 'operations',
      operations: primary,
      orderCoverage: computeOrderCoverage(primary),
      crewRequirements,
      legacy
    }
  }

  return {
    mode: 'legacy',
    operations: [],
    orderCoverage: 'none',
    crewRequirements,
    legacy
  }
}
